import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Ticket, Plus, Trash2, Save, Loader2, Info, Sparkles, Eye,
} from 'lucide-react';
import { API } from '../../App';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const emptyOffer = () => ({
  id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  title: '',
  description: '',
  tokens: 1,
});

const ProfessionalVitalPassOffersPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/professional/me`);
        setProfile(data);
        setOffers(data.vitalpass_offers || []);
      } catch (err) {
        toast.error('Erro ao carregar');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addOffer = () => setOffers((os) => [...os, emptyOffer()]);
  const removeOffer = (id) => setOffers((os) => os.filter((o) => o.id !== id));
  const updateOffer = (id, field, val) =>
    setOffers((os) => os.map((o) => (o.id === id ? { ...o, [field]: val } : o)));

  const handleSave = async () => {
    for (const o of offers) {
      if (!o.title.trim()) {
        toast.error('Cada oferta precisa de um título');
        return;
      }
      const t = Number(o.tokens);
      if (!Number.isFinite(t) || t < 1) {
        toast.error(`"${o.title}" precisa de ao menos 1 token`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/professional/profile`, {
        vitalpass_offers: offers.map((o) => ({
          id: o.id.startsWith('new_') ? undefined : o.id,
          title: o.title,
          description: o.description,
          tokens: Number(o.tokens) || 1,
        })),
      });
      const saved = data.professional?.vitalpass_offers || [];
      setOffers(saved);
      toast.success('Ofertas salvas — já visíveis para os alunos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="pro-vitalpass-offers-page">
      {/* Header */}
      <Card className="p-5 mb-4 border-slate-200 bg-gradient-to-br from-blue-50 to-white">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-slate-900 flex items-center justify-center flex-shrink-0">
            <Ticket className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest font-bold text-blue-600 mb-0.5">OFERTAS VITALPASS</p>
            <h1 className="text-xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              O que o aluno recebe por cada VITALPASS?
            </h1>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              Descreva os pacotes que você aceita em VITALPASS. Ex.: <em>1 token = sessão de massagem 60min</em>{' '}
              ou <em>1 token = avaliação física + 3 aulas acompanhadas</em>.
            </p>
          </div>
        </div>
      </Card>

      {/* Info card about payout */}
      <Card className="p-3 mb-4 border-emerald-200 bg-emerald-50/60">
        <div className="flex items-center gap-2 text-xs text-emerald-800">
          <Info className="w-4 h-4 flex-shrink-0" />
          <p><strong>Repasse:</strong> você recebe <strong>R$ 130,00 fixo por token consumido</strong>, independente da oferta. Configure pacotes que façam sentido para esse valor.</p>
        </div>
      </Card>

      {/* Offers list */}
      <div className="space-y-3 mb-4">
        {offers.length === 0 ? (
          <Card className="p-8 text-center border-dashed border-2 border-slate-200" data-testid="offers-empty">
            <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-1 font-semibold">Você ainda não cadastrou ofertas</p>
            <p className="text-xs text-slate-400">Clique em &quot;Adicionar oferta&quot; para começar</p>
          </Card>
        ) : (
          offers.map((o, i) => (
            <Card key={o.id} className="p-4 border-slate-200" data-testid={`offer-${i}`}>
              <div className="flex items-start gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-black text-sm flex-shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-slate-600 mb-1 block">Título da oferta *</Label>
                  <Input
                    value={o.title}
                    onChange={(e) => updateOffer(o.id, 'title', e.target.value)}
                    placeholder="Ex.: Sessão de massagem relaxante"
                    className="border-slate-300 focus:border-blue-500"
                    maxLength={120}
                    data-testid={`offer-${i}-title`}
                  />
                </div>
                <button
                  onClick={() => removeOffer(o.id)}
                  className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 flex items-center justify-center transition-colors flex-shrink-0 mt-6"
                  data-testid={`offer-${i}-remove`}
                  aria-label="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="sm:col-span-3">
                  <Label className="text-xs font-semibold text-slate-600 mb-1 block">Descrição / o que está incluso</Label>
                  <textarea
                    value={o.description}
                    onChange={(e) => updateOffer(o.id, 'description', e.target.value)}
                    placeholder="Ex.: 60 minutos, presencial. Inclui aromaterapia e óleos essenciais."
                    rows={2}
                    maxLength={400}
                    className="w-full text-sm rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                    data-testid={`offer-${i}-desc`}
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1 block flex items-center gap-1">
                    <Ticket className="w-3 h-3" /> Tokens *
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={o.tokens}
                    onChange={(e) => updateOffer(o.id, 'tokens', e.target.value)}
                    className="border-slate-300 focus:border-blue-500 font-bold text-center"
                    data-testid={`offer-${i}-tokens`}
                  />
                  <p className="text-[10px] text-slate-400 mt-1 text-center">
                    = R$ {(Number(o.tokens) * 130 || 130).toFixed(0)},00
                  </p>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Button
          onClick={addOffer}
          variant="outline"
          className="border-2 border-dashed border-blue-300 text-blue-700 hover:bg-blue-50 font-bold"
          data-testid="add-offer-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Adicionar oferta
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-blue-600 to-slate-900 hover:from-blue-700 hover:to-slate-800 text-white font-bold h-11 shadow"
          data-testid="save-offers-btn"
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</> : <><Save className="w-4 h-4 mr-2" /> Salvar ofertas</>}
        </Button>
      </div>

      {/* Preview */}
      {offers.length > 0 && (
        <Card className="p-4 border-slate-200 bg-slate-50">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Como o aluno vê
          </p>
          <div className="space-y-2">
            {offers.filter((o) => o.title.trim()).map((o) => (
              <div key={o.id} className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="flex items-start justify-between gap-2 mb-0.5">
                  <p className="font-bold text-sm text-slate-900">{o.title}</p>
                  <span className="text-[10px] uppercase font-black bg-blue-600 text-white px-2 py-0.5 rounded-full flex items-center gap-0.5 flex-shrink-0">
                    <Ticket className="w-2.5 h-2.5" /> {o.tokens}× VP
                  </span>
                </div>
                {o.description && <p className="text-xs text-slate-500 leading-relaxed">{o.description}</p>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProfessionalVitalPassOffersPage;
